import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "@/services/api";
import { useDashboardToasts } from "@/pages/dashboard/sections/useBillingToasts";
import { trackEvent } from "@/lib/analytics";
import { buildPurchaseEvent, shouldFirePurchase, shouldResumeCheckoutReconciliation } from "@/lib/conversionEvents";

/** Durable handle on an in-flight checkout, so the return can be verified whatever page it lands on. */
const ATTEMPT_KEY = "billing.checkoutAttemptId";
/** Where the user was when they started checkout, so we can put them back after confirming. */
const ORIGIN_KEY = "billing.checkoutOrigin";
/**
 * The plan tier being bought, stashed purely so the `purchase` event has a dimension to report —
 * never read for anything that decides the checkout's outcome. Reconcile's own {@code status} is
 * still the only thing that decides whether the purchase happened.
 */
const PLAN_TIER_KEY = "billing.checkoutPlanTier";
/**
 * When this attempt was remembered, so a later page load can bound how long it is willing to
 * resume polling for (see RESUME_MAX_AGE_MS) rather than resurrecting a genuinely abandoned
 * checkout indefinitely.
 */
const STORED_AT_KEY = "billing.checkoutStoredAtMs";
/** purchase already reported for these checkout attempt ids this session (survives re-renders, not reloads — a fresh load has no attemptId left to match once clearAttempt() has run). */
const purchaseFiredAttemptIds = new Set<string>();
/**
 * How long a page load without the one-shot return param is still willing to ask the backend
 * about a stored attempt, instead of assuming it was abandoned. 30 minutes covers a realistic
 * reload/re-navigate during or shortly after checkout without keeping a truly stale attempt (the
 * user closed the tab days ago and never came back) alive forever — matches the TTL this codebase
 * already uses for the analogous booking-return-state resume in BookingContext.tsx.
 */
const RESUME_MAX_AGE_MS = 30 * 60 * 1000;

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // localStorage unavailable (private mode / disabled).
    return null;
  }
}

function clearStored(key: string) {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}

/**
 * Records an in-flight checkout before handing off to the provider.
 *
 * <p>The origin path matters because the provider's return_url is a single fixed address
 * (the billing page) — it cannot vary per purchase without handing a client-supplied URL to
 * the payment provider, which is an open-redirect waiting to happen. So the return always
 * lands on billing, and this is what lets us send the user back to the screen they actually
 * bought the feature for.
 *
 * <p>{@code planTier} is optional and purely descriptive — carried through only so a confirmed
 * purchase can report which tier was bought. Its absence never blocks confirmation.
 */
export function rememberCheckout(attemptId: string, originPath: string, planTier?: string) {
  try {
    window.localStorage.setItem(ATTEMPT_KEY, attemptId);
    window.localStorage.setItem(ORIGIN_KEY, originPath);
    window.localStorage.setItem(STORED_AT_KEY, String(Date.now()));
    if (planTier) window.localStorage.setItem(PLAN_TIER_KEY, planTier);
  } catch {
    // Without storage the ?checkout= param still drives a plain refresh on the billing page.
  }
}

interface CheckoutReturnState {
  /** A verification poll is in flight — surfaces as a banner and holds billing cards at skeleton. */
  confirming: boolean;
}

const CheckoutReturnContext = createContext<CheckoutReturnState>({ confirming: false });

/** Whether a checkout return is currently being verified. */
export function useCheckoutReturn() {
  return useContext(CheckoutReturnContext);
}

/**
 * Verifies a return from the payment provider, wherever in the dashboard the user lands.
 *
 * <p>This used to live inside the billing section, which meant it only ran when the user was
 * looking at that page. A host who bought the branding add-on from the widget builder got a
 * correctly-charged, correctly-reconciled account and a UI that still showed the feature
 * locked — indistinguishable from a failed purchase. Mounting it on the dashboard chrome
 * makes the verification independent of which section is open.
 *
 * <p>The provider redirect is never trusted as proof of payment: the status/subscription_id
 * params only tell us a return happened. Confirmation is the POST to reconcile, which does a
 * real provider read server-side.
 */
export function CheckoutReturnProvider({ children }: { children: React.ReactNode }) {
  const [confirming, setConfirming] = useState(false);
  const handled = useRef(false);
  const cancelled = useRef(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toasts = useDashboardToasts();
  // The hook returns a fresh object each render; only these three are useCallback-stable, so
  // depend on them rather than on `toasts` (which would re-run this effect every render).
  const { success: toastSuccess, error: toastError, info: toastInfo } = toasts;

  useEffect(() => {
    if (handled.current) return;

    const params = new URLSearchParams(window.location.search);
    // Dodo does not preserve our return_url query string: it redirects back with its own
    // params (subscription_id / status / email) and drops ?checkout=success. Treat either
    // shape as a checkout return, otherwise the poll never starts and the state only
    // converges on the 15-minute reconciliation cron.
    const providerStatus = params.get("status");
    const providerSubscriptionId = params.get("subscription_id");
    const checkoutParam =
      params.get("checkout")
      ?? (providerSubscriptionId
        ? (providerStatus === "active" || providerStatus === "succeeded" ? "success" : "cancelled")
        : null);

    // Clean the return params immediately so a refresh doesn't replay them.
    if (params.has("checkout") || providerSubscriptionId) {
      params.delete("checkout");
      params.delete("subscription_id");
      params.delete("status");
      params.delete("email");
      const next = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (next ? `?${next}` : ""));
    }

    const attemptId = readStored(ATTEMPT_KEY);
    const originPath = readStored(ORIGIN_KEY);
    const planTier = readStored(PLAN_TIER_KEY);
    const storedAtRaw = readStored(STORED_AT_KEY);
    const storedAtMs = storedAtRaw == null ? null : Number(storedAtRaw);
    const clearAttempt = () => {
      clearStored(ATTEMPT_KEY);
      clearStored(ORIGIN_KEY);
      clearStored(PLAN_TIER_KEY);
      clearStored(STORED_AT_KEY);
    };

    if (checkoutParam === "cancelled") {
      clearAttempt();
      toastInfo("Checkout cancelled", "No changes were made to your plan.");
      return;
    }

    // No provider return param on this load. Not necessarily "nothing to verify" — an earlier
    // mount in this same session may have already consumed ?checkout=success and started polling
    // (in which case there is nothing left to do here), or the polling mount reloaded/navigated
    // away before reconciliation finished, leaving a genuinely live attempt in storage with no
    // return param left to prove it. shouldResumeCheckoutReconciliation tells these apart by the
    // attempt's own age — bounded so a truly abandoned checkout from days ago is dropped, not
    // resurrected and polled forever. Never treats the stored attempt itself as proof of anything;
    // it only decides whether asking the backend again is worth it. reconcile's own status remains
    // the only thing that can fire `purchase` (see shouldFirePurchase inside poll()).
    const shouldResume = checkoutParam !== "success" && shouldResumeCheckoutReconciliation({
      hasReturnParam: checkoutParam != null,
      storedAttemptId: attemptId,
      storedAtMs,
      nowMs: Date.now(),
      maxAgeMs: RESUME_MAX_AGE_MS,
    });

    if (checkoutParam !== "success" && !shouldResume) {
      // Nothing to verify and nothing worth resuming (an ordinary page load, or a stale/expired
      // leftover attempt). A leftover attempt id must not start a poll on its own, or the
      // confirming banner shows on a normal visit; drop it instead.
      if (attemptId) clearAttempt();
      return;
    }

    if (!attemptId) {
      // Success hint but no attempt id (e.g. localStorage blocked): fall back to a plain refresh.
      void queryClient.invalidateQueries({ queryKey: ["billing-overview"] });
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      return;
    }

    // Claim the run only now that this mount is definitely about to poll — either a fresh
    // provider return or a resumed attempt. Claiming any earlier meant StrictMode's double-invoke
    // (or an HMR remount) could burn the guard on a run with nothing to act on, so the actual
    // return or resume was skipped and the poll never started.
    handled.current = true;
    setConfirming(true);
    // Resuming gets the same 60s budget as a fresh return — it is not "starting over", it's
    // picking the same verification back up; the attempt itself may already be SUCCEEDED
    // server-side and resolve on the very first reconcile call below.
    const deadline = Date.now() + 60_000; // poll up to ~60s

    /**
     * Returns the user to whichever screen they started checkout from. Entitlements are
     * refetched first so the feature they just paid for is already unlocked when they arrive,
     * rather than briefly rendering its locked state.
     */
    const returnToOrigin = () => {
      if (!originPath) return;
      // Same-origin, app-relative paths only: this value is ours, but it is read back out of
      // storage, so treat it as untrusted rather than feeding anything to navigate(). "//host"
      // is rejected because the browser reads it as a protocol-relative absolute URL.
      if (!originPath.startsWith("/") || originPath.startsWith("//")) return;
      // Already there (the user started checkout from billing itself) — navigating would be a
      // pointless remount, and would discard the params we just cleaned off the URL.
      if (originPath === window.location.pathname + window.location.search) return;
      navigate(originPath, { replace: true });
    };

    const poll = async () => {
      if (cancelled.current) return;
      try {
        const res = await api.reconcileCheckoutAttempt(attemptId);
        if (cancelled.current) return;
        if (res.status === "SUCCEEDED") {
          // The only place `purchase` may fire: res.status here is the backend's own reconciled
          // read of the payment provider, never the provider's redirect query params (checked
          // and discarded above), which only prove a return happened. Deduped on attemptId at
          // module scope — not the per-mount `handled` ref — so a second poll tick or a
          // StrictMode remount reporting the same already-succeeded attempt cannot double-fire.
          if (shouldFirePurchase(res.status, attemptId, purchaseFiredAttemptIds)) {
            purchaseFiredAttemptIds.add(attemptId);
            const event = buildPurchaseEvent(attemptId, res.currency, planTier);
            trackEvent(event.name, event.params);
          }
          clearAttempt();
          // Await the refetch before dropping the banner. Clearing it first swapped the
          // skeleton back to the *old* plan for a beat, then snapped to the new one once the
          // queries landed. `me` carries entitlements, so it is what unlocks the feature.
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["billing-overview"] }),
            queryClient.invalidateQueries({ queryKey: ["billing-receipts"] }),
            queryClient.invalidateQueries({ queryKey: ["me"] }),
          ]);
          if (cancelled.current) return;
          setConfirming(false);
          toastSuccess("Payment confirmed", "Your plan is up to date — you’re all set.");
          returnToOrigin();
          return;
        }
        if (res.status === "FAILED" || res.status === "EXPIRED") {
          setConfirming(false);
          clearAttempt();
          toastError("Payment not completed", "We couldn’t confirm your payment. Please try again.");
          return;
        }
      } catch {
        // Transient error — keep trying until the deadline.
      }
      if (Date.now() >= deadline) {
        // Give up polling in the foreground. The backend's reconciliation cron still converges
        // this on its own, so the charge is never lost — only this screen stops waiting.
        setConfirming(false);
        clearAttempt();
        void queryClient.invalidateQueries({ queryKey: ["billing-overview"] });
        void queryClient.invalidateQueries({ queryKey: ["me"] });
        toastInfo(
          "Still confirming your payment",
          "This is taking a moment. Your plan will update automatically once confirmed.",
        );
        return;
      }
      window.setTimeout(poll, 2_000);
    };
    void poll();

    // No cleanup that cancels the poll. StrictMode unmounts and remounts in dev, and the
    // `handled` guard (correctly) stops the second run from starting a new poll — so
    // cancelling here left the banner up with nothing left to clear it. The poll is
    // self-limiting via its 60s deadline and is safe to let finish.
  }, [toastSuccess, toastError, toastInfo, queryClient, navigate]);

  return (
    <CheckoutReturnContext.Provider value={{ confirming }}>
      {children}
      {toasts.viewport}
    </CheckoutReturnContext.Provider>
  );
}
